/**
 * Which frontend build a document belongs to: the distinct content-hashed
 * names of its main bundle, polyfills and global stylesheet, sorted. Any code
 * change reaches `main-*`, since hashes propagate through imports. An empty
 * marker means an unhashed build (`ng serve`, the `local` configuration).
 *
 * It is a set because the built `index.html` names the stylesheet twice, once
 * inside `<noscript>`: a document parsed with `DOMParser` turns that copy into
 * an element, while the live document keeps it as text.
 */
export type BuildMarker = readonly string[];

const HASHED_NAME =
  /(?:^|\/)((?:main|polyfills)-[A-Z0-9]{8}\.js|styles-[A-Z0-9]{8}\.css)$/;
const MAIN_NAME = /^main-/;

export function buildMarker(doc: Document): BuildMarker {
  const names = new Set<string>();
  doc.querySelectorAll('script[src], link[href]').forEach((element) => {
    const url = element.getAttribute('src') ?? element.getAttribute('href');
    const match = url && HASHED_NAME.exec(url.split(/[?#]/)[0]);
    if (match) {
      names.add(match[1]);
    }
  });
  return [...names].sort();
}

export function hasMainBundle(marker: BuildMarker): boolean {
  return marker.some((name) => MAIN_NAME.test(name));
}

export function sameMarker(a: BuildMarker, b: BuildMarker): boolean {
  const left = [...a].sort();
  const right = [...b].sort();
  return (
    left.length === right.length &&
    left.every((name, index) => name === right[index])
  );
}

/**
 * The translations file suffix for this build. Tying `de.json` to the running
 * bundle keeps a browser from pairing new code with an older, heuristically
 * cached copy that lacks the keys the code uses.
 */
export function translationsSuffix(marker: BuildMarker): string {
  const main = marker.find((name) => MAIN_NAME.test(name));
  return main ? `.json?v=${main}` : '.json';
}
